import { BadgeArtSpec, badgeArtVersion } from '../../../common/badge-art/index.js'
import { clearBadgeGl, setBadgeGl } from './badge-gl-registry.js'

/**
 * GL readiness lives in a data attribute, not a class: React re-renders of `BadgeArt` rewrite
 * `className` (e.g. the modal's preview switch) and would wipe an imperative class, which
 * made the canvas vanish behind the SVG exactly when the switch flipped.
 */
export const READY_ATTR = 'data-gl-ready'

type GlModule = typeof import('./badge-gl.js')
let glModule: Promise<GlModule> | null = null
/** Imported on first use; note Bun's HTML bundling does not split it, so three.js is still in the main bundle (accepted). */
const loadGl = (): Promise<GlModule> => (glModule = glModule || import('./badge-gl.js'))

const io =
    typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
              (entries) =>
                  entries.forEach((e) => {
                      const h = handleOf.get(e.target)
                      if (!h) return
                      if (e.isIntersecting) h.show()
                      else h.hide()
                  }),
              { rootMargin: '200px' },
          )
        : null

const ro =
    typeof ResizeObserver === 'function'
        ? new ResizeObserver((entries) =>
              entries.forEach((e) => {
                  const w = Math.round(e.contentRect.width)
                  const last = widthOf.get(e.target)
                  widthOf.set(e.target, w)
                  if (last !== undefined && last !== w) handleOf.get(e.target)?.invalidate()
              }),
          )
        : null

const handleOf = new WeakMap<
    Element,
    { show: () => void; hide: () => void; invalidate: () => void }
>()
const widthOf = new WeakMap<Element, number>()

/**
 * Give an active `.badge-art` element a WebGL canvas over its SVG. Returns the cleanup.
 * No-op where WebGL is unavailable (the SVG stays, as in the prototype's fallback).
 */
export const mountBadgeArt = (el: HTMLElement, spec: BadgeArtSpec): (() => void) => {
    let cancelled = false
    let cleanup: (() => void) | null = null
    void loadGl().then((gl) => {
        if (cancelled || !gl.webglAvailable()) return
        const cv = document.createElement('canvas')
        cv.className = 'badge-art__gl'
        cv.setAttribute('aria-hidden', 'true')
        el.appendChild(cv)
        const h = gl.createBadgeGl(cv, spec, {
            onReady: () => el.setAttribute(READY_ATTR, ''),
            onStale: () => el.removeAttribute(READY_ATTR),
        })
        setBadgeGl(el, h)
        handleOf.set(el, h)
        if (io) io.observe(el)
        else h.show()
        ro?.observe(el)
        cleanup = () => {
            io?.unobserve(el)
            ro?.unobserve(el)
            handleOf.delete(el)
            widthOf.delete(el)
            clearBadgeGl(el)
            h.dispose()
            cv.remove()
            el.removeAttribute(READY_ATTR)
        }
    })
    return () => {
        cancelled = true
        cleanup?.()
    }
}

type MountRef = (el: HTMLElement | null) => (() => void) | undefined
const refCache = new Map<string, MountRef>()

/**
 * Callback ref for `BadgeArt`, memoised per art + state. React treats a ref callback with a
 * new identity as detach + attach, so an inline arrow would tear the canvas down and bake it
 * again on every parent render (e.g. the whole grid blinking when the modal switches badge).
 */
export const badgeArtMountRef = (spec: BadgeArtSpec, state: 'active' | 'slot'): MountRef => {
    const key = `${badgeArtVersion(spec)}|${state}`
    let ref = refCache.get(key)
    if (!ref) {
        ref = (el) => (el && state === 'active' ? mountBadgeArt(el, spec) : undefined)
        refCache.set(key, ref)
    }
    return ref
}
